import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useWebPush } from '@/hooks/useWebPush';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { isInstallOfferBlocked, isIos, isStandalone } from '@/pwa/pwaEnvironment';
import { claimPrompt, releasePrompt, getActivePrompt } from '@/pwa/promptCoordinator';

const TOAST_ID = 'push-permission-prompt';
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const key = (userId: string) => `push-prompt-dismissed:${userId}`;
const EXCLUDED = [
  '/login', '/escolher-ambiente', '/esqueci-senha', '/redefinir-senha', '/cadastro-corretor',
  '/corretor', '/super-admin', '/politica-de-privacidade', '/diagnostico',
];
const isExcluded = (path: string) => path === '/' || EXCLUDED.some((p) => path === p || path.startsWith(p + '/'));

/** Outros convites (instalar app / nova versão) visíveis na tela. */
const otherPromptVisible = () =>
  (getActivePrompt() !== null && getActivePrompt() !== 'push') ||
  Array.from(document.querySelectorAll('button')).some((b) =>
    /^(Atualizar agora|Atualizando\.\.\.)$/.test((b.textContent || '').trim()));

const hide = (motivo: string) => console.info('[push-prompt] oculto:', motivo);

export const PushPermissionToast: React.FC = () => {
  const { user } = useSupabaseAuth();
  const userId = user?.id;
  const { state, enable } = useWebPush(userId);
  const { pathname } = useLocation();
  const shownRef = useRef(false);
  const enableRef = useRef(enable);
  enableRef.current = enable;

  const excluded = isExcluded(pathname);

  useEffect(() => {
    if (excluded) toast.dismiss(TOAST_ID);
  }, [excluded]);

  useEffect(() => {
    if (shownRef.current) return;
    if (!userId) { hide('sem usuário logado'); return; }
    if (isInstallOfferBlocked()) { hide('preview/iframe/dev'); return; }
    if (excluded) { hide(`rota excluída ${pathname}`); return; }
    // iPhone/iPad fora do app instalado: só o convite de instalação.
    if (isIos() && !isStandalone()) { hide('iOS fora do app instalado'); return; }
    if (state !== 'off') { hide(`state=${state}`); return; }
    try {
      const last = Number(localStorage.getItem(key(userId)) || 0);
      if (last && Date.now() - last < WEEK_MS) {
        hide(`adiado até ${new Date(last + WEEK_MS).toLocaleString('pt-BR')}`);
        return;
      }
    } catch { /* ignore */ }

    let timer: number;
    const tryShow = (attempt: number) => {
      if (otherPromptVisible() && attempt < 20) {
        if (attempt === 0) hide('aguardando outro convite sair');
        timer = window.setTimeout(() => tryShow(attempt + 1), 4000);
        return;
      }
      if (!claimPrompt('push')) { timer = window.setTimeout(() => tryShow(attempt + 1), 4000); return; }
      shownRef.current = true;
      show(userId);
    };
    timer = window.setTimeout(() => tryShow(0), 3000);
    return () => window.clearTimeout(timer);
  }, [userId, state, excluded, pathname]);

  const show = (uid: string) => {
    const standalone = isStandalone();
    const dismiss = () => {
      try { localStorage.setItem(key(uid), String(Date.now())); } catch { /* ignore */ }
      toast.dismiss(TOAST_ID);
      releasePrompt('push');
    };
    const accept = async () => {
      // Fecha o convite na hora; a ativação continua em segundo plano.
      toast.dismiss(TOAST_ID);
      releasePrompt('push');
      try { localStorage.setItem(key(uid), String(Date.now())); } catch { /* ignore */ }
      const ok = await enableRef.current();
      if (ok) { toast.success(standalone ? 'Notificações ativadas neste aparelho' : 'Notificações ativadas neste navegador'); return; }
      const permission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';
      // Janela do Chrome fechada sem escolher: fecha o convite em silêncio.
      if (permission === 'default') return;
      toast.error(permission === 'denied'
        ? 'Notificações bloqueadas. Para liberar, clique no cadeado à esquerda do endereço do site → Notificações → Permitir.'
        : 'Não foi possível ativar as notificações neste navegador.');
    };

    toast.custom(() => (
      <div className="w-[min(360px,calc(100vw-2rem))] rounded-lg border border-border bg-background p-4 text-foreground shadow-lg">
        <div className="flex gap-3">
          <Bell className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold tracking-wide text-foreground">{standalone ? 'ATIVAR NOTIFICAÇÕES NESTE APARELHO' : 'ATIVAR NOTIFICAÇÕES NO NAVEGADOR'}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {pathname.startsWith('/portal-corretor')
                  ? 'Receba avisos de novos veículos, preços reduzidos e reservas mesmo com o portal fechado.'
                  : 'Receba avisos de vendas, tarefas e chamados mesmo com o AutoFluxo em segundo plano.'}
            </p>
            <div className="mt-3 flex items-center gap-2">
              <Button size="sm" className="h-9 flex-[1.4] font-semibold" onClick={accept}>SIM</Button>
              <Button size="sm" variant="ghost" className="h-9 flex-1 bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground" onClick={dismiss}>
                NÃO
              </Button>
            </div>
          </div>
        </div>
      </div>
    ), { id: TOAST_ID, duration: Infinity, position: 'bottom-center', style: { marginBottom: 'env(safe-area-inset-bottom)' }, unstyled: true, className: '!bg-transparent !border-0 !shadow-none !p-0' });
  };

  return null;
};

export default PushPermissionToast;
